import { Component, OnInit, signal, inject, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { Subject, takeUntil } from 'rxjs';
import { MessageService } from 'primeng/api';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import {
  SearchFormComponent,
  SearchFormData,
  FormFieldConfig,
} from '@shared/components/search-form/search-form.component';
import {
  DataTableComponent,
  TableColumn,
} from '@shared/components/data-table/data-table.component';
import {
  ConfirmationDialogComponent,
  ConfirmationButton,
} from '@shared/components/confirmation-dialog/confirmation-dialog.component';
import { SaleReportService } from './sale-report.service';

// Chart Components
import { BarChartComponent } from '../../../../shared/components/charts/bar-chart/bar-chart.component';
import { LineChartComponent } from '../../../../shared/components/charts/line-chart/line-chart.component';
import { PieChartComponent } from '../../../../shared/components/charts/pie-chart/pie-chart.component';
import {
  RadarChartComponent,
  RadarChartData,
} from '../../../../shared/components/charts/radar-chart/radar-chart.component';
import { PanelItem } from '../../../../shared/components/charts/panel-group/panel-group.component';
import { ProgressItem } from '../../../../shared/components/charts/box-card/box-card.component';
import {
  SaleReportRecord,
  SalesAnalysisRecord,
  ProductSalesRecord,
  ShopSalesRecord,
  StatisticsOverview,
  SALE_REPORT_DEFAULT_CONFIG,
  TREND_INDICATORS,
  SaleReportSearchParams,
} from './sale-report.config';

@Component({
  selector: 'app-sale-report',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    ToastModule,
    TranslateModule,
    SearchFormComponent,
    DataTableComponent,
    ConfirmationDialogComponent,
    // Chart Components
    BarChartComponent,
    LineChartComponent,
    PieChartComponent,
    RadarChartComponent,
  ],
  providers: [MessageService],
  templateUrl: './sale-report.component.html',
  styleUrl: './sale-report.component.css',
})
export class SaleReportComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly saleReportService = inject(SaleReportService);
  private readonly messageService = inject(MessageService);
  private readonly translateService = inject(TranslateService);
  private readonly destroy$ = new Subject<void>();

  // Report type management
  activeReportType = signal<'statistics' | 'analysis' | 'products' | 'shops'>(
    'statistics',
  );

  // Report tabs configuration
  reportTabs = [
    { label: 'sale_report.tabs.sales_statistics', value: 'statistics' },
    { label: 'sale_report.tabs.sales_analysis', value: 'analysis' },
    { label: 'sale_report.tabs.product_sales', value: 'products' },
    { label: 'sale_report.tabs.shop_sales', value: 'shops' },
  ];

  // Data signals for different report types
  statisticsData = signal<SaleReportRecord[]>([]);
  analysisData = signal<SalesAnalysisRecord[]>([]);
  productData = signal<ProductSalesRecord[]>([]);
  shopData = signal<ShopSalesRecord[]>([]);

  // Statistics overview
  statisticsOverview = signal<StatisticsOverview | null>(null);

  // Common data signals
  loading = signal(false);
  totalRecords = signal(0);
  first = signal(0);
  rows = signal(SALE_REPORT_DEFAULT_CONFIG.PAGE_SIZE);

  // Configuration constants (for template access)
  readonly SALE_REPORT_DEFAULT_CONFIG = SALE_REPORT_DEFAULT_CONFIG;
  readonly TREND_INDICATORS = TREND_INDICATORS;

  // Chart Data Properties
  salesTrendChartData = {
    expectedData: [120, 132, 101, 134, 90, 230, 210],
    actualData: [220, 182, 191, 234, 290, 330, 310],
  };

  salesByPaymentMethodData = {
    series: [
      {
        name: 'Sales by Payment Method',
        type: 'pie',
        roseType: 'radius',
        radius: [15, 95],
        center: ['50%', '38%'],
        data: [
          { value: 320, name: 'Credit Card' },
          { value: 240, name: 'eWallet' },
          { value: 149, name: 'COD' },
          { value: 100, name: 'Bank Transfer' },
          { value: 59, name: 'Other' },
        ],
        animationEasing: 'cubicInOut',
        animationDuration: 2600,
      },
    ],
  };

  salesByRegionData = {
    xAxis: [
      {
        type: 'category',
        data: ['North', 'South', 'East', 'West', 'Central'],
        axisTick: { alignWithLabel: true },
      },
    ],
    yAxis: [{ type: 'value', axisTick: { show: false } }],
    series: [
      {
        name: 'Sales',
        type: 'bar',
        barWidth: '60%',
        data: [79, 52, 200, 334, 390],
        animationDuration: 6000,
      },
    ],
  };

  orderConversionData = {
    series: [
      {
        name: 'Conversion Funnel',
        type: 'funnel',
        left: '10%',
        top: 60,
        width: '80%',
        height: '80%',
        min: 0,
        max: 100,
        minSize: '0%',
        maxSize: '100%',
        sort: 'descending',
        gap: 2,
        label: {
          show: true,
          position: 'inside',
        },
        labelLine: {
          length: 10,
          lineStyle: {
            width: 1,
            type: 'solid',
          },
        },
        itemStyle: {
          borderColor: '#fff',
          borderWidth: 1,
        },
        emphasis: {
          label: {
            fontSize: 20,
          },
        },
        data: [
          { value: 100, name: 'Visits' },
          { value: 80, name: 'Browsing' },
          { value: 60, name: 'Add to Cart' },
          { value: 40, name: 'Checkout' },
          { value: 20, name: 'Completed' },
        ],
      },
    ],
  };

  topProductsData = {
    xAxis: [
      {
        type: 'category',
        data: ['iPhone 15', 'MacBook Pro', 'AirPods', 'iPad', 'Watch'],
        axisTick: { alignWithLabel: true },
      },
    ],
    yAxis: [{ type: 'value', axisTick: { show: false } }],
    series: [
      {
        name: 'Sales',
        type: 'bar',
        barWidth: '60%',
        data: [156, 89, 234, 120, 78],
        animationDuration: 6000,
      },
    ],
  };

  bottomProductsData = {
    xAxis: [
      {
        type: 'category',
        data: [
          'Old Model Phone',
          'Outdated Laptop',
          'Legacy Headphones',
          'Old Tablet',
          'Discontinued Watch',
        ],
        axisTick: { alignWithLabel: true },
      },
    ],
    yAxis: [{ type: 'value', axisTick: { show: false } }],
    series: [
      {
        name: 'Sales',
        type: 'bar',
        barWidth: '60%',
        data: [12, 8, 15, 6, 4],
        animationDuration: 6000,
        itemStyle: {
          color: '#ef4444', // Red color for slow-moving products
        },
      },
    ],
  };

  shopPerformanceData: RadarChartData = {
    radar: {
      radius: '66%',
      center: ['50%', '42%'],
      splitNumber: 8,
      indicator: [
        { name: 'Sales', max: 10000 },
        { name: 'Orders', max: 2000 },
        { name: 'Customers', max: 5000 },
        { name: 'Satisfaction', max: 100 },
        { name: 'Efficiency', max: 100 },
      ],
    },
    legend: {
      left: 'center',
      bottom: '10%',
      data: ['Tmall Store', 'JD Store'],
    },
    series: [
      {
        name: 'Tmall Store',
        value: [8000, 1500, 4000, 85, 90],
      },
      {
        name: 'JD Store',
        value: [6000, 1200, 3000, 80, 85],
      },
    ],
  };

  // KPI Panels Data
  kpiPanels: PanelItem[] = [
    {
      icon: 'pi pi-dollar',
      iconClass: 'icon-money',
      title: 'sale_report.kpi.total_sales',
      value: 89456,
      color: '#f4516c',
      type: 'sales',
    },
    {
      icon: 'pi pi-shopping-cart',
      iconClass: 'icon-shopping',
      title: 'sale_report.kpi.total_orders',
      value: 1234,
      color: '#34bfa3',
      type: 'orders',
    },
    {
      icon: 'pi pi-users',
      iconClass: 'icon-people',
      title: 'sale_report.kpi.total_customers',
      value: 987,
      color: '#40c9c6',
      type: 'customers',
    },
    {
      icon: 'pi pi-chart-line',
      iconClass: 'icon-message',
      title: 'sale_report.kpi.conversion_rate',
      value: 15.2,
      color: '#36a3f7',
      type: 'conversion',
    },
  ];

  // Progress Items for Box Card
  progressItems: ProgressItem[] = [
    {
      label: 'sale_report.progress.sales_target',
      percentage: 85,
      status: 'success',
    },
    {
      label: 'sale_report.progress.order_fulfillment',
      percentage: 92,
      status: 'success',
    },
    {
      label: 'sale_report.progress.customer_satisfaction',
      percentage: 78,
      status: 'warning',
    },
    {
      label: 'sale_report.progress.inventory_turnover',
      percentage: 65,
      status: 'exception',
    },
  ];

  // Confirmation dialog properties
  showConfirmationDialog = false;
  selectedRecord: any = null;
  dialogTitle = '';
  dialogMessage = '';
  dialogButtons: ConfirmationButton[] = [];
  currentAction: 'export' | 'delete' | null = null;

  ngOnInit(): void {
    this.updateChartDataLabels();
    this.loadData();
  }

  private updateChartDataLabels(): void {
    // Update payment method chart data - create new object reference
    this.salesByPaymentMethodData = {
      ...this.salesByPaymentMethodData,
      series: [
        {
          ...this.salesByPaymentMethodData.series[0],
          data: [
            {
              value: 320,
              name: this.translateService.instant(
                'sale_report.chart_data.payment_methods.credit_card',
              ),
            },
            {
              value: 240,
              name: this.translateService.instant(
                'sale_report.chart_data.payment_methods.ewallet',
              ),
            },
            {
              value: 149,
              name: this.translateService.instant(
                'sale_report.chart_data.payment_methods.cod',
              ),
            },
            {
              value: 100,
              name: this.translateService.instant(
                'sale_report.chart_data.payment_methods.bank_transfer',
              ),
            },
            {
              value: 59,
              name: this.translateService.instant(
                'sale_report.chart_data.payment_methods.other',
              ),
            },
          ],
        },
      ],
    };

    // Update region chart data - create new object reference
    this.salesByRegionData = {
      ...this.salesByRegionData,
      xAxis: [
        {
          ...this.salesByRegionData.xAxis[0],
          data: [
            this.translateService.instant(
              'sale_report.chart_data.regions.north',
            ),
            this.translateService.instant(
              'sale_report.chart_data.regions.south',
            ),
            this.translateService.instant(
              'sale_report.chart_data.regions.east',
            ),
            this.translateService.instant(
              'sale_report.chart_data.regions.west',
            ),
            this.translateService.instant(
              'sale_report.chart_data.regions.central',
            ),
          ],
        },
      ],
    };

    // Update conversion funnel data - create new object reference
    this.orderConversionData = {
      ...this.orderConversionData,
      series: [
        {
          ...this.orderConversionData.series[0],
          data: [
            {
              value: 100,
              name: this.translateService.instant(
                'sale_report.chart_data.conversion_funnel.visits',
              ),
            },
            {
              value: 80,
              name: this.translateService.instant(
                'sale_report.chart_data.conversion_funnel.browsing',
              ),
            },
            {
              value: 60,
              name: this.translateService.instant(
                'sale_report.chart_data.conversion_funnel.add_to_cart',
              ),
            },
            {
              value: 40,
              name: this.translateService.instant(
                'sale_report.chart_data.conversion_funnel.checkout',
              ),
            },
            {
              value: 20,
              name: this.translateService.instant(
                'sale_report.chart_data.conversion_funnel.completed',
              ),
            },
          ],
        },
      ],
    };

    // Update top products data - create new object reference
    this.topProductsData = {
      ...this.topProductsData,
      xAxis: [
        {
          ...this.topProductsData.xAxis[0],
          data: [
            this.translateService.instant(
              'sale_report.chart_data.top_products.iphone_15',
            ),
            this.translateService.instant(
              'sale_report.chart_data.top_products.macbook_pro',
            ),
            this.translateService.instant(
              'sale_report.chart_data.top_products.airpods',
            ),
            this.translateService.instant(
              'sale_report.chart_data.top_products.ipad',
            ),
            this.translateService.instant(
              'sale_report.chart_data.top_products.watch',
            ),
          ],
        },
      ],
    };

    // Update bottom products data - create new object reference
    this.bottomProductsData = {
      ...this.bottomProductsData,
      xAxis: [
        {
          ...this.bottomProductsData.xAxis[0],
          data: [
            this.translateService.instant(
              'sale_report.chart_data.bottom_products.old_model_phone',
            ),
            this.translateService.instant(
              'sale_report.chart_data.bottom_products.outdated_laptop',
            ),
            this.translateService.instant(
              'sale_report.chart_data.bottom_products.legacy_headphones',
            ),
            this.translateService.instant(
              'sale_report.chart_data.bottom_products.old_tablet',
            ),
            this.translateService.instant(
              'sale_report.chart_data.bottom_products.discontinued_watch',
            ),
          ],
        },
      ],
    };

    // Update radar chart - create new object reference
    this.shopPerformanceData = {
      ...this.shopPerformanceData,
      radar: this.shopPerformanceData.radar
        ? {
            ...this.shopPerformanceData.radar,
            indicator: [
              {
                name: this.translateService.instant(
                  'sale_report.chart_data.radar_indicators.sales',
                ),
                max: 10000,
              },
              {
                name: this.translateService.instant(
                  'sale_report.chart_data.radar_indicators.orders',
                ),
                max: 2000,
              },
              {
                name: this.translateService.instant(
                  'sale_report.chart_data.radar_indicators.customers',
                ),
                max: 5000,
              },
              {
                name: this.translateService.instant(
                  'sale_report.chart_data.radar_indicators.satisfaction',
                ),
                max: 100,
              },
              {
                name: this.translateService.instant(
                  'sale_report.chart_data.radar_indicators.efficiency',
                ),
                max: 100,
              },
            ],
          }
        : undefined,
      legend: this.shopPerformanceData.legend
        ? {
            ...this.shopPerformanceData.legend,
            data: [
              this.translateService.instant(
                'sale_report.chart_data.radar_legends.tmall_store',
              ),
              this.translateService.instant(
                'sale_report.chart_data.radar_legends.jd_store',
              ),
            ],
          }
        : undefined,
      series: [
        {
          ...this.shopPerformanceData.series[0],
          name: this.translateService.instant(
            'sale_report.chart_data.radar_legends.tmall_store',
          ),
        },
        {
          ...this.shopPerformanceData.series[1],
          name: this.translateService.instant(
            'sale_report.chart_data.radar_legends.jd_store',
          ),
        },
      ],
    };
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSearch(searchData: SearchFormData): void {
    console.log('Search data:', searchData);
    this.first.set(0);
    this.loadData(searchData);
  }

  onReset(): void {
    this.first.set(0);
    this.loadData();
  }

  onPageChange(event: any): void {
    this.first.set(event.first);
    this.rows.set(event.rows);
    this.loadData();
  }

  onTabChange(event: { index: number }): void {
    const tab = this.reportTabs[event.index];
    this.activeReportType.set(
      tab.value as 'statistics' | 'analysis' | 'products' | 'shops',
    );
    this.first.set(0); // Reset to first page when changing report type
    this.loadData(); // Reload data for the new report type
  }

  onExport(): void {
    const reportTypeKey = `sale_report.tabs.${this.activeReportType()}`;
    const reportTypeLabel = this.translateService.instant(reportTypeKey);
    this.dialogTitle = this.translateService.instant(
      'sale_report.dialog.confirm_export',
    );
    this.dialogMessage = this.translateService.instant(
      'sale_report.dialog.export_message',
      { reportType: reportTypeLabel },
    );
    this.currentAction = 'export';
    this.dialogButtons = [
      {
        label: this.translateService.instant('sale_report.actions.cancel'),
        action: 'cancel',
        outlined: true,
      },
      {
        label: this.translateService.instant('sale_report.actions.export'),
        action: 'confirm',
        severity: 'info',
      },
    ];
    this.showConfirmationDialog = true;
  }

  private loadData(searchParams?: SearchFormData): void {
    this.loading.set(true);

    const params: SaleReportSearchParams = {
      page: Math.floor(this.first() / this.rows()),
      size: this.rows(),
      ...searchParams,
    };

    const reportType = this.activeReportType();

    switch (reportType) {
      case 'statistics':
        this.loadStatisticsData(params);
        break;
      case 'analysis':
        this.loadAnalysisData(params);
        break;
      case 'products':
        this.loadProductData(params);
        break;
      case 'shops':
        this.loadShopData(params);
        break;
    }
  }

  private loadStatisticsData(params: SaleReportSearchParams): void {
    // Load statistics overview
    this.saleReportService
      .getStatisticsOverview()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (overview) => {
          this.statisticsOverview.set(overview);
        },
        error: (error) => {
          console.error('Failed to load statistics overview:', error);
        },
      });

    // Load detailed statistics data
    this.saleReportService
      .getSalesStatistics(params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.statisticsData.set(response.content);
          this.totalRecords.set(response.totalElements);
          this.loading.set(false);
        },
        error: (error) => {
          this.loading.set(false);
          console.error('Failed to load statistics data:', error);
        },
      });
  }

  private loadAnalysisData(params: SaleReportSearchParams): void {
    this.saleReportService
      .getSalesAnalysis(params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.analysisData.set(response.content);
          this.totalRecords.set(response.totalElements);
          this.loading.set(false);
        },
        error: (error) => {
          this.loading.set(false);
          console.error('Failed to load analysis data:', error);
        },
      });
  }

  private loadProductData(params: SaleReportSearchParams): void {
    this.saleReportService
      .getProductSales(params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.productData.set(response.content);
          this.totalRecords.set(response.totalElements);
          this.loading.set(false);
        },
        error: (error) => {
          this.loading.set(false);
          console.error('Failed to load product data:', error);
        },
      });
  }

  private loadShopData(params: SaleReportSearchParams): void {
    this.saleReportService
      .getShopSales(params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.shopData.set(response.content);
          this.totalRecords.set(response.totalElements);
          this.loading.set(false);
        },
        error: (error) => {
          this.loading.set(false);
          console.error('Failed to load shop data:', error);
        },
      });
  }

  // Utility methods
  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  }

  formatPercentage(value: number): string {
    return `${value}%`;
  }

  getTrendIndicator(trend: 'up' | 'down' | 'stable'): string {
    return TREND_INDICATORS[trend] || '→ Stable';
  }

  getTrendClass(trend: 'up' | 'down' | 'stable'): string {
    switch (trend) {
      case 'up':
        return 'text-green-600';
      case 'down':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  }

  // Confirmation dialog methods
  onConfirmatiButtonClick(action: string): void {
    if (action === 'cancel') {
      this.closeConfirmationDialog();
    } else if (action === 'confirm') {
      if (this.currentAction === 'export') {
        this.confirmExport();
      } else if (this.currentAction === 'delete') {
        this.confirmDelete();
      }
    }
  }

  confirmExport(): void {
    this.saleReportService
      .exportSaleReport({})
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blob) => {
          // Create download link
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${this.activeReportType()}-report.csv`;
          link.click();
          window.URL.revokeObjectURL(url);

          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.success'),
            detail: this.translateService.instant(
              'sale_report.messages.export_success',
            ),
          });
        },
        error: (error) => {
          console.error('Failed to export report:', error);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.error'),
            detail: this.translateService.instant(
              'sale_report.messages.export_failed',
            ),
          });
        },
      });
    this.closeConfirmationDialog();
  }

  confirmDelete(): void {
    // In a real application, you would call the delete service
    console.log('Deleting record:', this.selectedRecord?.id);

    this.messageService.add({
      severity: 'success',
      summary: this.translateService.instant('common.success'),
      detail: this.translateService.instant(
        'sale_report.messages.delete_success',
      ),
    });

    this.loadData(); // Reload data
    this.closeConfirmationDialog();
  }

  closeConfirmationDialog(): void {
    this.showConfirmationDialog = false;
    this.selectedRecord = null;
    this.currentAction = null;
    this.dialogTitle = '';
    this.dialogMessage = '';
    this.dialogButtons = [];
  }

  // Panel click handler
  onPanelClick(type: string): void {
    console.log('Panel clicked:', type);
    // Handle different panel types
    switch (type) {
      case 'sales':
        this.messageService.add({
          severity: 'warn',
          summary: this.translateService.instant(
            'sale_report.messages.sales_panel',
          ),
          detail: this.translateService.instant(
            'sale_report.messages.sales_panel_not_implemented',
          ),
        });
        break;
      case 'orders':
        this.messageService.add({
          severity: 'warn',
          summary: this.translateService.instant(
            'sale_report.messages.orders_panel',
          ),
          detail: this.translateService.instant(
            'sale_report.messages.orders_panel_not_implemented',
          ),
        });
        break;
      case 'customers':
        this.messageService.add({
          severity: 'warn',
          summary: this.translateService.instant(
            'sale_report.messages.customers_panel',
          ),
          detail: this.translateService.instant(
            'sale_report.messages.customers_panel_not_implemented',
          ),
        });
        break;
      case 'conversion':
        this.messageService.add({
          severity: 'warn',
          summary: this.translateService.instant(
            'sale_report.messages.conversion_panel',
          ),
          detail: this.translateService.instant(
            'sale_report.messages.conversion_panel_not_implemented',
          ),
        });
        break;
    }
  }

  // Helper methods for template
  getSearchConfig(): any[] {
    const reportType = this.activeReportType();
    switch (reportType) {
      case 'statistics':
        return this.getSalesStatisticsSearchConfig();
      case 'analysis':
        return this.getSalesAnalysisSearchConfig();
      case 'products':
        return this.getProductSalesSearchConfig();
      case 'shops':
        return this.getShopSalesSearchConfig();
      default:
        return [];
    }
  }

  private getSalesStatisticsSearchConfig(): FormFieldConfig[] {
    return [
      {
        key: 'dateRange',
        label: 'sale_report.search.statistics.date_range',
        type: 'dateRange',
        range: { startKey: 'startDate', endKey: 'endDate' },
      },
      {
        key: 'orderStatus',
        label: 'sale_report.search.statistics.order_status',
        type: 'select',
        placeholder: 'sale_report.search.statistics.all_statuses',
        options: [
          {
            label: this.translateService.instant(
              'sale_report.search.statistics.all_statuses',
            ),
            value: 'ALL',
          },
          {
            label: this.translateService.instant(
              'sale_report.search.statistics.pending',
            ),
            value: 'PENDING',
          },
          {
            label: this.translateService.instant(
              'sale_report.search.statistics.confirmed',
            ),
            value: 'CONFIRMED',
          },
          {
            label: this.translateService.instant(
              'sale_report.search.statistics.processing',
            ),
            value: 'PROCESSING',
          },
          {
            label: this.translateService.instant(
              'sale_report.search.statistics.shipped',
            ),
            value: 'SHIPPED',
          },
          {
            label: this.translateService.instant(
              'sale_report.search.statistics.delivered',
            ),
            value: 'DELIVERED',
          },
          {
            label: this.translateService.instant(
              'sale_report.search.statistics.cancelled',
            ),
            value: 'CANCELLED',
          },
          {
            label: this.translateService.instant(
              'sale_report.search.statistics.refunded',
            ),
            value: 'REFUNDED',
          },
        ],
      },
      {
        key: 'paymentMethod',
        label: 'sale_report.search.statistics.payment_method',
        type: 'select',
        placeholder: 'sale_report.search.statistics.all_methods',
        options: [
          {
            label: this.translateService.instant(
              'sale_report.search.statistics.all_methods',
            ),
            value: 'ALL',
          },
          {
            label: this.translateService.instant(
              'sale_report.search.statistics.credit_card',
            ),
            value: 'CREDIT_CARD',
          },
          {
            label: this.translateService.instant(
              'sale_report.search.statistics.ewallet',
            ),
            value: 'EWALLET',
          },
          {
            label: this.translateService.instant(
              'sale_report.search.statistics.cod',
            ),
            value: 'COD',
          },
          {
            label: this.translateService.instant(
              'sale_report.search.statistics.bank_transfer',
            ),
            value: 'BANK_TRANSFER',
          },
          {
            label: this.translateService.instant(
              'sale_report.search.statistics.other',
            ),
            value: 'OTHER',
          },
        ],
      },
      {
        key: 'channel',
        label: 'sale_report.search.statistics.channel',
        type: 'select',
        placeholder: 'sale_report.search.statistics.all_channels',
        options: [
          {
            label: this.translateService.instant(
              'sale_report.search.statistics.all_channels',
            ),
            value: 'ALL',
          },
          {
            label: this.translateService.instant(
              'sale_report.search.statistics.online_store',
            ),
            value: 'ONLINE',
          },
          {
            label: this.translateService.instant(
              'sale_report.search.statistics.mobile_app',
            ),
            value: 'MOBILE',
          },
          {
            label: this.translateService.instant(
              'sale_report.search.statistics.physical_store',
            ),
            value: 'PHYSICAL',
          },
          {
            label: this.translateService.instant(
              'sale_report.search.statistics.marketplace',
            ),
            value: 'MARKETPLACE',
          },
        ],
      },
    ];
  }

  private getSalesAnalysisSearchConfig(): FormFieldConfig[] {
    return [
      {
        key: 'timeDimension',
        label: 'sale_report.search.analysis.time_dimension',
        type: 'select',
        placeholder: 'sale_report.search.analysis.daily',
        options: [
          {
            label: this.translateService.instant(
              'sale_report.search.analysis.daily',
            ),
            value: 'DAILY',
          },
          {
            label: this.translateService.instant(
              'sale_report.search.analysis.weekly',
            ),
            value: 'WEEKLY',
          },
          {
            label: this.translateService.instant(
              'sale_report.search.analysis.monthly',
            ),
            value: 'MONTHLY',
          },
          {
            label: this.translateService.instant(
              'sale_report.search.analysis.yearly',
            ),
            value: 'YEARLY',
          },
        ],
      },
      {
        key: 'channel',
        label: 'sale_report.search.analysis.channel',
        type: 'select',
        placeholder: 'sale_report.search.analysis.all_channels',
        options: [
          {
            label: this.translateService.instant(
              'sale_report.search.analysis.all_channels',
            ),
            value: 'ALL',
          },
          {
            label: this.translateService.instant(
              'sale_report.search.analysis.online_store',
            ),
            value: 'ONLINE',
          },
          {
            label: this.translateService.instant(
              'sale_report.search.analysis.mobile_app',
            ),
            value: 'MOBILE',
          },
          {
            label: this.translateService.instant(
              'sale_report.search.analysis.physical_store',
            ),
            value: 'PHYSICAL',
          },
          {
            label: this.translateService.instant(
              'sale_report.search.analysis.marketplace',
            ),
            value: 'MARKETPLACE',
          },
        ],
      },
      {
        key: 'regionArea',
        label: 'sale_report.search.analysis.region_area',
        type: 'select',
        placeholder: 'sale_report.search.analysis.all_regions',
        options: [
          {
            label: this.translateService.instant(
              'sale_report.search.analysis.all_regions',
            ),
            value: 'ALL',
          },
          {
            label: this.translateService.instant(
              'sale_report.search.analysis.north_america',
            ),
            value: 'NORTH_AMERICA',
          },
          {
            label: this.translateService.instant(
              'sale_report.search.analysis.europe',
            ),
            value: 'EUROPE',
          },
          {
            label: this.translateService.instant(
              'sale_report.search.analysis.asia_pacific',
            ),
            value: 'ASIA_PACIFIC',
          },
          {
            label: this.translateService.instant(
              'sale_report.search.analysis.latin_america',
            ),
            value: 'LATIN_AMERICA',
          },
          {
            label: this.translateService.instant(
              'sale_report.search.analysis.middle_east',
            ),
            value: 'MIDDLE_EAST',
          },
          {
            label: this.translateService.instant(
              'sale_report.search.analysis.africa',
            ),
            value: 'AFRICA',
          },
        ],
      },
    ];
  }

  private getProductSalesSearchConfig(): FormFieldConfig[] {
    return [
      {
        key: 'productName',
        label: 'sale_report.search.products.product_name',
        type: 'text',
        placeholder: 'sale_report.search.products.enter_keywords',
      },
      {
        key: 'productId',
        label: 'sale_report.search.products.product_id_sku',
        type: 'text',
        placeholder: 'sale_report.search.products.enter_code',
      },
      {
        key: 'category',
        label: 'sale_report.search.products.category',
        type: 'select',
        placeholder: 'sale_report.search.products.all_categories',
        options: [
          {
            label: this.translateService.instant(
              'sale_report.search.products.all_categories',
            ),
            value: 'ALL',
          },
          {
            label: this.translateService.instant(
              'sale_report.search.products.electronics',
            ),
            value: 'ELECTRONICS',
          },
          {
            label: this.translateService.instant(
              'sale_report.search.products.clothing',
            ),
            value: 'CLOTHING',
          },
          {
            label: this.translateService.instant(
              'sale_report.search.products.home_garden',
            ),
            value: 'HOME_GARDEN',
          },
          {
            label: this.translateService.instant(
              'sale_report.search.products.sports',
            ),
            value: 'SPORTS',
          },
          {
            label: this.translateService.instant(
              'sale_report.search.products.beauty',
            ),
            value: 'BEAUTY',
          },
          {
            label: this.translateService.instant(
              'sale_report.search.products.books',
            ),
            value: 'BOOKS',
          },
          {
            label: this.translateService.instant(
              'sale_report.search.products.automotive',
            ),
            value: 'AUTOMOTIVE',
          },
        ],
      },
      {
        key: 'brand',
        label: 'sale_report.search.products.brand',
        type: 'select',
        placeholder: 'sale_report.search.products.all_brands',
        options: [
          {
            label: this.translateService.instant(
              'sale_report.search.products.all_brands',
            ),
            value: 'ALL',
          },
          {
            label: this.translateService.instant(
              'sale_report.search.products.apple',
            ),
            value: 'APPLE',
          },
          {
            label: this.translateService.instant(
              'sale_report.search.products.samsung',
            ),
            value: 'SAMSUNG',
          },
          {
            label: this.translateService.instant(
              'sale_report.search.products.nike',
            ),
            value: 'NIKE',
          },
          {
            label: this.translateService.instant(
              'sale_report.search.products.adidas',
            ),
            value: 'ADIDAS',
          },
          {
            label: this.translateService.instant(
              'sale_report.search.products.sony',
            ),
            value: 'SONY',
          },
          {
            label: this.translateService.instant(
              'sale_report.search.products.lg',
            ),
            value: 'LG',
          },
          {
            label: this.translateService.instant(
              'sale_report.search.products.other',
            ),
            value: 'OTHER',
          },
        ],
      },
      {
        key: 'topSellingFlag',
        label: 'sale_report.search.products.top_selling_flag',
        type: 'select',
        placeholder: 'sale_report.search.products.all_products',
        options: [
          {
            label: this.translateService.instant(
              'sale_report.search.products.all_products',
            ),
            value: 'ALL',
          },
          {
            label: this.translateService.instant(
              'sale_report.search.products.top_selling_only',
            ),
            value: 'true',
          },
          {
            label: this.translateService.instant(
              'sale_report.search.products.non_top_selling',
            ),
            value: 'false',
          },
        ],
      },
    ];
  }

  private getShopSalesSearchConfig(): FormFieldConfig[] {
    return [
      {
        key: 'shopStoreName',
        label: 'sale_report.search.shops.shop_store_name',
        type: 'text',
        placeholder: 'sale_report.search.shops.enter_keywords',
      },
      {
        key: 'channel',
        label: 'sale_report.search.shops.channel',
        type: 'select',
        placeholder: 'sale_report.search.shops.all_channels',
        options: [
          {
            label: this.translateService.instant(
              'sale_report.search.shops.all_channels',
            ),
            value: 'ALL',
          },
          {
            label: this.translateService.instant(
              'sale_report.search.shops.online_store',
            ),
            value: 'ONLINE',
          },
          {
            label: this.translateService.instant(
              'sale_report.search.shops.mobile_app',
            ),
            value: 'MOBILE',
          },
          {
            label: this.translateService.instant(
              'sale_report.search.shops.physical_store',
            ),
            value: 'PHYSICAL',
          },
          {
            label: this.translateService.instant(
              'sale_report.search.shops.marketplace',
            ),
            value: 'MARKETPLACE',
          },
          {
            label: this.translateService.instant(
              'sale_report.search.shops.franchise',
            ),
            value: 'FRANCHISE',
          },
        ],
      },
      {
        key: 'region',
        label: 'sale_report.search.shops.region',
        type: 'select',
        placeholder: 'sale_report.search.shops.all_regions',
        options: [
          {
            label: this.translateService.instant(
              'sale_report.search.shops.all_regions',
            ),
            value: 'ALL',
          },
          {
            label: this.translateService.instant(
              'sale_report.search.shops.north_america',
            ),
            value: 'NORTH_AMERICA',
          },
          {
            label: this.translateService.instant(
              'sale_report.search.shops.europe',
            ),
            value: 'EUROPE',
          },
          {
            label: this.translateService.instant(
              'sale_report.search.shops.asia_pacific',
            ),
            value: 'ASIA_PACIFIC',
          },
          {
            label: this.translateService.instant(
              'sale_report.search.shops.latin_america',
            ),
            value: 'LATIN_AMERICA',
          },
          {
            label: this.translateService.instant(
              'sale_report.search.shops.middle_east',
            ),
            value: 'MIDDLE_EAST',
          },
          {
            label: this.translateService.instant(
              'sale_report.search.shops.africa',
            ),
            value: 'AFRICA',
          },
        ],
      },
      {
        key: 'status',
        label: 'sale_report.search.shops.status',
        type: 'select',
        placeholder: 'sale_report.search.shops.all_status',
        options: [
          {
            label: this.translateService.instant(
              'sale_report.search.shops.all_status',
            ),
            value: 'ALL',
          },
          {
            label: this.translateService.instant(
              'sale_report.search.shops.active',
            ),
            value: 'ACTIVE',
          },
          {
            label: this.translateService.instant(
              'sale_report.search.shops.inactive',
            ),
            value: 'INACTIVE',
          },
          {
            label: this.translateService.instant(
              'sale_report.search.shops.suspended',
            ),
            value: 'SUSPENDED',
          },
          {
            label: this.translateService.instant(
              'sale_report.search.shops.under_review',
            ),
            value: 'UNDER_REVIEW',
          },
        ],
      },
    ];
  }

  getTableTitle(): string {
    const reportType = this.activeReportType();
    switch (reportType) {
      case 'statistics':
        return this.translateService.instant(
          'sale_report.table_titles.detailed_data',
        );
      case 'analysis':
        return this.translateService.instant(
          'sale_report.table_titles.analysis_results',
        );
      case 'products':
        return this.translateService.instant(
          'sale_report.table_titles.product_sales_ranking',
        );
      case 'shops':
        return this.translateService.instant(
          'sale_report.table_titles.shop_sales_data',
        );
      default:
        return this.translateService.instant(
          'sale_report.table_titles.data_table',
        );
    }
  }

  getTableTitleClass(): string {
    const reportType = this.activeReportType();
    switch (reportType) {
      case 'statistics':
        return 'title-statistics';
      case 'analysis':
        return 'title-analysis';
      case 'products':
        return 'title-products';
      case 'shops':
        return 'title-shops';
      default:
        return 'title-default';
    }
  }

  getTableColumns(): any[] {
    const reportType = this.activeReportType();
    switch (reportType) {
      case 'statistics':
        return this.getSalesStatisticsTableConfig();
      case 'analysis':
        return this.getSalesAnalysisTableConfig();
      case 'products':
        return this.getProductSalesTableConfig();
      case 'shops':
        return this.getShopSalesTableConfig();
      default:
        return [];
    }
  }

  private getSalesStatisticsTableConfig(): TableColumn[] {
    return [
      {
        label: 'sale_report.table.statistics.order_id',
        prop: 'orderId',
        width: 120,
      },
      {
        label: 'sale_report.table.statistics.order_date',
        prop: 'orderDate',
        width: 120,
      },
      {
        label: 'sale_report.table.statistics.order_status',
        prop: 'orderStatus',
        width: 120,
      },
      {
        label: 'sale_report.table.statistics.payment_method',
        prop: 'paymentMethod',
        width: 130,
      },
      {
        label: 'sale_report.table.statistics.channel',
        prop: 'channel',
        width: 120,
      },
      {
        label: 'sale_report.table.statistics.gross_sales',
        prop: 'grossSales',
        width: 120,
        styleClass: 'text-right',
      },
      {
        label: 'sale_report.table.statistics.discount',
        prop: 'discount',
        width: 100,
        styleClass: 'text-right',
      },
      {
        label: 'sale_report.table.statistics.refund',
        prop: 'refund',
        width: 100,
        styleClass: 'text-right',
      },
      {
        label: 'sale_report.table.statistics.net_sales',
        prop: 'netSales',
        width: 120,
        styleClass: 'text-right',
      },
      {
        label: 'sale_report.table.statistics.tax',
        prop: 'tax',
        width: 80,
        styleClass: 'text-right',
      },
      {
        label: 'sale_report.table.statistics.shipping_fee',
        prop: 'shippingFee',
        width: 110,
        styleClass: 'text-right',
      },
      {
        label: 'sale_report.table.statistics.total_collected',
        prop: 'totalCollected',
        width: 130,
        styleClass: 'text-right',
      },
      {
        label: 'sale_report.table.statistics.number_of_orders',
        prop: 'numberOfOrders',
        width: 130,
        styleClass: 'text-right',
      },
      {
        label: 'sale_report.table.statistics.average_order_value',
        prop: 'averageOrderValue',
        width: 180,
        styleClass: 'text-right',
      },
    ];
  }

  private getSalesAnalysisTableConfig(): TableColumn[] {
    return [
      {
        label: 'sale_report.table.analysis.date_period',
        prop: 'datePeriod',
        width: 150,
      },
      {
        label: 'sale_report.table.analysis.channel',
        prop: 'channel',
        width: 120,
      },
      {
        label: 'sale_report.table.analysis.region_area',
        prop: 'regionArea',
        width: 140,
      },
      {
        label: 'sale_report.table.analysis.total_orders',
        prop: 'totalOrders',
        width: 120,
        styleClass: 'text-right',
      },
      {
        label: 'sale_report.table.analysis.gross_sales',
        prop: 'grossSales',
        width: 120,
        styleClass: 'text-right',
      },
      {
        label: 'sale_report.table.analysis.net_sales',
        prop: 'netSales',
        width: 120,
        styleClass: 'text-right',
      },
      {
        label: 'sale_report.table.analysis.refund',
        prop: 'refund',
        width: 100,
        styleClass: 'text-right',
      },
      {
        label: 'sale_report.table.analysis.average_order_value',
        prop: 'averageOrderValue',
        width: 180,
        styleClass: 'text-right',
      },
      {
        label: 'sale_report.table.analysis.conversion_rate',
        prop: 'conversionRate',
        width: 150,
        styleClass: 'text-right',
      },
      {
        label: 'sale_report.table.analysis.sales_growth_rate',
        prop: 'salesGrowthRate',
        width: 160,
        styleClass: 'text-right',
      },
    ];
  }

  private getProductSalesTableConfig(): TableColumn[] {
    return [
      {
        label: 'sale_report.table.products.product_id_sku',
        prop: 'productId',
        width: 150,
      },
      {
        label: 'sale_report.table.products.product_name',
        prop: 'productName',
        width: 200,
      },
      {
        label: 'sale_report.table.products.category',
        prop: 'category',
        width: 120,
      },
      { label: 'sale_report.table.products.brand', prop: 'brand', width: 120 },
      {
        label: 'sale_report.table.products.quantity_sold',
        prop: 'quantitySold',
        width: 130,
        styleClass: 'text-right',
      },
      {
        label: 'sale_report.table.products.returned_quantity',
        prop: 'returnedQuantity',
        width: 140,
        styleClass: 'text-right',
      },
      {
        label: 'sale_report.table.products.unit_price',
        prop: 'unitPrice',
        width: 110,
        styleClass: 'text-right',
      },
      {
        label: 'sale_report.table.products.gross_sales',
        prop: 'grossSales',
        width: 120,
        styleClass: 'text-right',
      },
      {
        label: 'sale_report.table.products.discount_applied',
        prop: 'discountApplied',
        width: 140,
        styleClass: 'text-right',
      },
      {
        label: 'sale_report.table.products.net_sales',
        prop: 'netSales',
        width: 120,
        styleClass: 'text-right',
      },
      {
        label: 'sale_report.table.products.top_selling_flag',
        prop: 'topSellingFlag',
        width: 140,
      },
    ];
  }

  private getShopSalesTableConfig(): TableColumn[] {
    return [
      {
        label: 'sale_report.table.shops.shop_store_name',
        prop: 'shopStoreName',
        width: 180,
      },
      { label: 'sale_report.table.shops.channel', prop: 'channel', width: 120 },
      { label: 'sale_report.table.shops.region', prop: 'region', width: 120 },
      {
        label: 'sale_report.table.shops.number_of_orders',
        prop: 'numberOfOrders',
        width: 140,
        styleClass: 'text-right',
      },
      {
        label: 'sale_report.table.shops.gross_sales',
        prop: 'grossSales',
        width: 120,
        styleClass: 'text-right',
      },
      {
        label: 'sale_report.table.shops.net_sales',
        prop: 'netSales',
        width: 120,
        styleClass: 'text-right',
      },
      {
        label: 'sale_report.table.shops.refund',
        prop: 'refund',
        width: 100,
        styleClass: 'text-right',
      },
      {
        label: 'sale_report.table.shops.shipping_fee',
        prop: 'shippingFee',
        width: 120,
        styleClass: 'text-right',
      },
      {
        label: 'sale_report.table.shops.top_selling_products',
        prop: 'topSellingProducts',
        width: 180,
      },
      {
        label: 'sale_report.table.shops.sales_growth_rate',
        prop: 'salesGrowthRate',
        width: 160,
        styleClass: 'text-right',
      },
    ];
  }

  formatDate(dateString: string): string {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  }
}
